/**
 * evacuationProfiles.js v1.0.0 - 2026-10-13 (Tab 2 plan, step 5: evacuation profiles by cause)
 *
 * Evacuation is delayed for different reasons in different phases, so the delay is modelled by CAUSE. Each cause acts on
 * one of the two delay slots of the wound hazard model (and of the T1 survival curve):
 *
 *   (A) TRANSPORT-limited   acts on the time to ARRIVAL at the first facility:
 *                           river crossing (no ground route across, vehicles forward, no safe airspace until a foothold),
 *                           amphibious (until the beachhead), airborne / air assault (until link-up), urban (casualty
 *                           collection between buildings), breach (one lane), march (columns moving).
 *                           arrival = hold hours + base arrival / availability
 *                           availability = the best of ground CASEVAC availability and (when helicopters exist) air availability.
 *                           The air situation and the enemy air defense already act on the MEDEVAC analysis, so they are not
 *                           multiplied in here again (each factor counts once).
 *   (B) FACILITY-limited    acts on the time to SURGERY: the medical unit that would operate is packed on its vehicles
 *                           (march, displacement). Uses the setup times and the setup ramp of casualtyCalculatorConstants
 *                           (AJMedP-1 3.4): delay = (1 - capacity at that day of the ramp) x setup hours of the surgical facility.
 *   (C) THREAT-limited      the evacuation paradigm, the hold hours and the drone schedule (exist today, not changed here);
 *                           assault on fortified / trench positions adds a hold.
 *
 * Every number below is an EDUCATED_GUESS (docs/planning/TAB2_PROPOSAL_TABLE.md layer D) except the setup times and the ramp (AJMedP-1).
 * Nothing is deleted: set USE_EVACUATION_PROFILES to false to restore the former behaviour.
 */

import {
  MTF_SETUP_TIMES_HOURS, MTF_SETUP_RAMP_R2B, MTF_SETUP_RAMP_R2E, MTF_SETUP_RAMP_R3
} from './casualtyCalculatorConstants';
import { DEFAULT_FOOTHOLD_DAY } from './phaseCasualtyTable';
import { rearLegExtraMinutes } from './threatEffects';

export const USE_EVACUATION_PROFILES = true;

export const EVACUATION_PROFILE_STATUS = Object.freeze({
  status: 'EDUCATED_GUESS',
  basis: 'Cause model agreed with Dr. Tatar 2026-10-13; values are planning guesses to be replaced by data. Setup times and ramp: AJMedP-1 3.4.'
});

/** Floor of the availability: with nothing available the delay is capped at 1 / floor times the base arrival time. */
export const AVAILABILITY_FLOOR = 0.1;

const NEUTRAL = Object.freeze({ hold_hours: 0, ground: 1, air: 1 });

/** Transport profile of a special condition on a day of the phase. */
export function transportRowFor(condition, dayInPhase, footholdDay) {
  switch (condition) {
    case 'RIVER_ASSAULT': {
      const s = dayInPhase - (footholdDay ?? DEFAULT_FOOTHOLD_DAY.RIVER_ASSAULT);   // days since the foothold
      if (s < 0) return { hold_hours: 6, ground: 0.2, air: 0.5 };
      if (s === 0) return { hold_hours: 3, ground: 0.6, air: 0.8 };
      if (s === 1) return { hold_hours: 1, ground: 1, air: 1 };
      return NEUTRAL;
    }
    case 'AMPHIBIOUS':
      if (dayInPhase <= 1) return { hold_hours: 8, ground: 0.1, air: 0.3 };
      if (dayInPhase === 2) return { hold_hours: 4, ground: 0.4, air: 0.6 };
      if (dayInPhase === 3) return { hold_hours: 2, ground: 0.7, air: 1 };
      return NEUTRAL;
    case 'AIRBORNE': {
      const linkUp = footholdDay ?? DEFAULT_FOOTHOLD_DAY.AIRBORNE;
      if (dayInPhase >= linkUp) return NEUTRAL;
      return { hold_hours: Math.max(4, 12 - 4 * (dayInPhase - 1)), ground: 0, air: 0.5 };
    }
    case 'URBAN':
      return { hold_hours: 1, ground: 0.7, air: 0.6 };
    case 'BREACH':
      return { hold_hours: 1, ground: 0.8, air: 1 };
    case 'FORTIFIED_ASSAULT':
      return { hold_hours: 2, ground: 1, air: 1 };   // cause C: threat; a hold only
    default:
      return NEUTRAL;
  }
}

/** Ground CASEVAC on a march: columns on the move (guess); air follows the air situation, which acts elsewhere. */
export const MARCH_TRANSPORT = Object.freeze({ hold_hours: 0, ground: 0.6, air: 1 });

/** Days between displacements of the supporting medical units when the phase keeps moving (guess); 1 = always moving. */
export const DISPLACEMENT_INTERVAL_DAYS = Object.freeze({
  EXPLOITATION: 2,
  PURSUIT: 2,
  DELAY: 2,
  WITHDRAWAL: 2,
  RETIREMENT: 1
});

/**
 * Setup hours and ramp of the facility that performs the surgery. Only a facility that moves with the maneuver force packs
 * when the force moves: the brigade medical company (Role 2 Basic, with or without the surgical team) always; the division
 * Role 2 Enhanced only when no other brigade stays behind (r2eMovesWithForce); the corps Role 3 stays open during tactical
 * movement (IMP-MARCH-001).
 */
export function facilityOf(surgicalDestination, r2eMovesWithForce = false) {
  const t = String(surgicalDestination || '');
  if (t.includes('ROLE3') || t.includes('ROLE_3')) return { name: 'R3', setup_hours: MTF_SETUP_TIMES_HOURS.ROLE_3, ramp: MTF_SETUP_RAMP_R3, moves_with_force: false };
  if (t.includes('ROLE2_ENHANCED') || t.includes('ROLE_2_ENHANCED')) return { name: 'R2E', setup_hours: MTF_SETUP_TIMES_HOURS.ROLE_2E, ramp: MTF_SETUP_RAMP_R2E, moves_with_force: r2eMovesWithForce };
  return { name: 'R2B', setup_hours: MTF_SETUP_TIMES_HOURS.ROLE_2B, ramp: MTF_SETUP_RAMP_R2B, moves_with_force: true };
}

/** Is the medical support packed today? march phases always; otherwise by the displacement interval of the form. */
export function isMedicalPackedToday({ isMarch, form, dayInPhase }) {
  if (isMarch) return true;
  const interval = DISPLACEMENT_INTERVAL_DAYS[form];
  return interval !== undefined && dayInPhase % interval === 0;
}

/**
 * Evacuation adjustment of one day.
 * @returns {{extra_arrival_min:number, extra_surgery_min:number, extra_total_min:number, transport:Object, facility:Object}}
 */
/**
 * Minutes of the legs from Role 2 upward on the way to the surgical facility (the legs the MSR / ASR serve). The Role 1 to Role 2
 * leg and the point-of-injury legs belong to the phase evacuation profile, not to the roads (Dr. Tatar 2026-10-13).
 */
export function rearLegMinutes(segments) {
  if (!Array.isArray(segments)) return 0;
  return segments
    .filter((seg) => /^ROLE_?(2|3|4)/.test(String(seg.from || '')))
    .reduce((sum, seg) => sum + (typeof seg.time_min === 'number' && Number.isFinite(seg.time_min) ? seg.time_min : 0), 0);
}

export function evacuationAdjustment({ condition, dayInPhase, footholdDay, isMarch, hasHelo, baseArrivalMin, surgicalDestination, packedToday, daysSincePacked, r2eMovesWithForce = false, helicopterFactor = 1, roadState = null, rearLegMin = 0, evacDelayFactor = 1 }) {
  if (typeof baseArrivalMin !== 'number' || !Number.isFinite(baseArrivalMin) || baseArrivalMin < 0) {
    throw new Error(`evacuationProfiles.evacuationAdjustment: baseArrivalMin must be a non-negative number (got ${baseArrivalMin})`);
  }
  const row = isMarch ? MARCH_TRANSPORT : transportRowFor(condition, dayInPhase, footholdDay);
  const availability = Math.max(AVAILABILITY_FLOOR, Math.max(row.ground, hasHelo ? row.air * helicopterFactor : 0));
  // evacDelayFactor: communications of the infrastructure card (x1.0 to x2.0 on the evacuation time)
  const extraArrivalMin = row.hold_hours * 60 + baseArrivalMin * (1 / availability - 1) + baseArrivalMin * (evacDelayFactor - 1);

  const facility = facilityOf(surgicalDestination, r2eMovesWithForce);
  const k = packedToday ? 0 : daysSincePacked;
  const capacity = !facility.moves_with_force || k === Infinity || k >= facility.ramp.length ? 1 : facility.ramp[Math.min(k, facility.ramp.length - 1)];
  // legs above Role 2 (MSR / ASR): time over speed x availability plus the delay (threatEffects.rearLegExtraMinutes)
  const rearExtraMin = roadState && rearLegMin > 0 ? rearLegExtraMinutes(roadState, rearLegMin) : 0;
  const extraSurgeryMin = (1 - capacity) * facility.setup_hours * 60 + rearExtraMin;

  return {
    extra_arrival_min: Math.round(extraArrivalMin),
    extra_surgery_min: Math.round(extraSurgeryMin),
    rear_legs: roadState ? { minutes: rearLegMin, extra_min: rearExtraMin, road_status: roadState.status, route: roadState.route } : null,
    extra_total_min: Math.round(extraArrivalMin + extraSurgeryMin),
    transport: { cause: 'TRANSPORT', condition: isMarch ? 'MARCH' : (condition || 'NONE'), hold_hours: row.hold_hours, ground: row.ground, air: row.air, helicopter_factor: helicopterFactor, availability },
    facility: { cause: 'FACILITY', facility: facility.name, moves_with_force: facility.moves_with_force, packed_today: Boolean(packedToday), days_since_packed: k === Infinity ? null : k, capacity, setup_hours: facility.setup_hours },
    status: EVACUATION_PROFILE_STATUS.status
  };
}
